/**
 * @mention helpers for listing comments (PedalDetails).
 *
 * Mentions are stored inline in the comment text as `@name` or, when the
 * name would not survive the `@\w+` parser, as `@[name]`. The parser,
 * renderer (renderCommentWithMentions) and server notifier
 * (functions/notifyListingComments.js) all understand both forms.
 *
 * Why this file exists (2026-10-06): the Reply button and the @ picker
 * only bracketed usernames containing a SPACE (commit 2b1263e). A username
 * like `mike-g` or `j.smith` went in as `@mike-g`, the parser kept `mike`,
 * and the person being replied to was never notified (and an unrelated
 * "mike" could be). Bracketing is now decided by one rule in one place.
 */

// A mention that needs no brackets: letters, digits, underscore only.
const PLAIN_USERNAME = /^\w+$/;

export function formatMention(username) {
  const name = String(username || '').trim();
  if (!name) return '';
  return PLAIN_USERNAME.test(name) ? `@${name}` : `@[${name}]`;
}

// Mention tokens as the comment parser recognises them, but only at the
// start of the text or after whitespace: the `@` inside jane@gmail.com is
// not a mention, and stripping it would hide the address from the
// contact-info filter. (The submit-time parser is deliberately looser and
// matches anywhere; it only decides who gets NOTIFIED, never what gets
// FILTERED, so the two need not agree.)
//
// And only when the token ENDS there (review finding, 2026-10-06): a plain
// `@\w+` stops at the first non-word character, so `@cheapgear.com` used to
// lose `@cheapgear` and leave a bare `.com` the domain filter no longer
// recognised. A mention may be followed by end of text, whitespace or
// punctuation, but not by a word character, another `@`, or a `.`/`-`
// that continues into a word. Same rule after a bracketed mention.
export const MENTION_TOKEN_REGEX = /(^|\s)(@\[([^\]]+)\]|@(\w+))(?![\w@]|[.-]\w)/g;

/**
 * The comment with KNOWN mention tokens removed, for content validation.
 *
 * Usernames are never run through the message filter, so a reply to
 * "Kiko" or "Damn Good Tone" used to fail validation on the pre-filled
 * mention alone (the filter saw "kik" / "damn") with a misleading "links
 * and contact info" error. Validate what the user typed, not who they
 * addressed.
 *
 * Only names in `allowedNames` are removed. The bracket syntax is plain
 * text anyone can type, so an unrestricted strip would let
 * `@[jane@gmail.com]` or `@[any banned words]` past the filter (review
 * finding, 2026-10-06). The caller passes the usernames that can
 * legitimately be addressed on this listing: the owner, everyone who has
 * commented, and anyone picked from the @ popup. Everything else stays in
 * the text and is filtered like any other words.
 *
 * @param {string} text
 * @param {Iterable<string>} allowedNames  usernames, compared case-insensitively
 */
export function stripMentions(text, allowedNames = []) {
  // Defence in depth: a name containing '@' is never a username (the
  // comment writer falls back to the account EMAIL when the profile has not
  // loaded, so stored comment usernames can be addresses). Never exempt one.
  const allowed = new Set(
    Array.from(allowedNames, (n) => String(n || '').trim().toLowerCase())
      .filter((n) => n && !n.includes('@'))
  );
  if (allowed.size === 0) return String(text || '');
  return String(text || '').replace(MENTION_TOKEN_REGEX, (whole, lead, _token, bracketed, plain) => {
    const name = (bracketed || plain || '').trim().toLowerCase();
    return allowed.has(name) ? `${lead} ` : whole;
  });
}

// Complete mention names in `text`, as the submit-time parser reads them
// (anywhere in the text, bracketed or plain), lower-cased.
function mentionNamesIn(text) {
  const re = /@\[([^\]]+)\]|@(\w+)/g;
  const names = new Set();
  let m;
  while ((m = re.exec(String(text || ''))) !== null) {
    names.add((m[1] || m[2]).trim().toLowerCase());
  }
  return names;
}

/**
 * Does `draft` already mention `username` as a COMPLETE mention?
 *
 * Review finding (2026-10-06): the Reply handler used
 * `draft.includes('@mike')`, which is true for a draft that mentions
 * `@mike_g2`, so replying to mike after mentioning mike_g2 silently did
 * nothing and mike was never notified.
 */
export function hasMention(draft, username) {
  const name = String(username || '').trim().toLowerCase();
  return !!name && mentionNamesIn(draft).has(name);
}

/** The Reply button's edit: prepend the mention unless already present. */
export function prependMention(draft, username) {
  const mention = formatMention(username);
  if (!mention || hasMention(draft, username)) return draft;
  const rest = String(draft || '').trim();
  return rest ? `${mention} ${rest}` : `${mention} `;
}

/**
 * The names the content filter may skip, built ONLY from canonical profile
 * usernames.
 *
 * Review finding (2026-10-06): exemptions were built from the `username`
 * stored on each comment, but that field falls back to the account email
 * when the writer's profile had not loaded. Stored comment usernames are
 * therefore not trustworthy as names. Callers pass `profileUsernames`,
 * looked up from publicProfiles by the commenters' and owner's uids, plus
 * names picked from the @ popup (which are themselves publicProfiles reads).
 *
 * @param {object} p
 * @param {Object<string, string|undefined>} p.profileUsernames  uid -> publicProfiles.username
 * @param {Iterable<string>} [p.pickedNames]
 * @returns {string[]}
 */
export function buildMentionExemptions({ profileUsernames = {}, pickedNames = [] }) {
  const out = new Set();
  for (const n of [...Object.values(profileUsernames), ...pickedNames]) {
    const name = typeof n === 'string' ? n.trim() : '';
    if (name && !name.includes('@')) out.add(name);
  }
  return [...out];
}

/**
 * The name Reply inserts for `comment`: the VERIFIED profile username only.
 * The `username` stored on the comment is never used, because it can be the
 * writer's email (review finding, 2026-10-06). Null means "open the comment
 * box without a mention".
 */
export function replyTargetName(participantUsernames, comment) {
  const name = participantUsernames?.[comment?.userId];
  return typeof name === 'string' && name.trim() ? name : null;
}
